/**
 * ControlsDebugOverlay - Debug component to visualize controls state and timeout
 * Shows real-time information about controls visibility, playback state, and timer
 */
import React, { useEffect, useState } from 'react';
import { usePlayerState, usePlayer } from '../core/PlayerContext';

function ControlsDebugOverlay() {
    var player = usePlayer();
    var controlsVisible = usePlayerState('controlsVisible');
    var paused = usePlayerState('paused');
    var currentTime = usePlayerState('currentTime');
    var duration = usePlayerState('duration');

    var [lastActivity, setLastActivity] = useState(Date.now());
    var [timeSinceActivity, setTimeSinceActivity] = useState(0);

    // Track user activity
    useEffect(function () {
        var updateActivity = function () {
            setLastActivity(Date.now());
        };

        window.addEventListener('mousemove', updateActivity);
        window.addEventListener('keydown', updateActivity);

        return function () {
            window.removeEventListener('mousemove', updateActivity);
            window.removeEventListener('keydown', updateActivity);
        };
    }, []);

    // Update time since last activity every 100ms
    useEffect(function () {
        var interval = setInterval(function () {
            setTimeSinceActivity(Date.now() - lastActivity);
        }, 100);

        return function () {
            clearInterval(interval);
        };
    }, [lastActivity]);

    var formatTime = function (ms) {
        return (ms / 1000).toFixed(1) + 's';
    };

    var formatVideoTime = function (seconds) {
        if (!seconds || isNaN(seconds)) return '0:00';
        var mins = Math.floor(seconds / 60);
        var secs = Math.floor(seconds % 60);
        return mins + ':' + (secs < 10 ? '0' : '') + secs;
    };

    return React.createElement('div', {
        style: {
            position: 'fixed',
            top: '10px',
            right: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '13px',
            zIndex: 99999,
            minWidth: '250px',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
        }
    },
        React.createElement('div', {
            style: {
                marginBottom: '8px',
                paddingBottom: '8px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                fontWeight: 'bold',
                color: '#4CAF50',
            }
        }, '🐛 Controls Debug'),

        // Manual toggle button to test CSS
        React.createElement('button', {
            onClick: function () {
                player.showControls();
                if (controlsVisible) {
                    player.hideControls(0);  // Hide immediately
                } else {
                    player.hideControls(10000);  // Stay visible for 10s
                }
            },
            style: {
                width: '100%',
                padding: '6px 10px',
                marginBottom: '8px',
                backgroundColor: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '12px'
            }
        }, '🔄 TOGGLE (Test CSS)'),

        // Controls Visible
        React.createElement('div', { style: { marginBottom: '6px' } },
            React.createElement('span', { style: { color: '#888' } }, 'Controls: '),
            React.createElement('span', {
            }, controlsVisible ? 'VISIBLE' : 'HIDDEN')
        ),

        // Playback State
        React.createElement('div', { style: { marginBottom: '6px' } },
            React.createElement('span', { style: { color: '#888' } }, 'Playback: '),
            React.createElement('span', {
                style: {
                    color: paused ? '#FF9800' : '#4CAF50',
                    fontWeight: 'bold'
                }
            }, paused ? 'PAUSED' : 'PLAYING')
        ),

        // Time Since Activity
        React.createElement('div', { style: { marginBottom: '6px' } },
            React.createElement('span', { style: { color: '#888' } }, 'Inactivity: '),
            React.createElement('span', {
                style: {
                    color: timeSinceActivity > 5000 ? '#f44336' : timeSinceActivity > 2500 ? '#FF9800' : '#4CAF50',
                    fontWeight: 'bold'
                }
            }, formatTime(timeSinceActivity)),
            React.createElement('span', {
                style: {
                    color: '#666',
                    fontSize: '11px',
                    marginLeft: '6px'
                }
            }, '/ 5.0s')
        ),

        // Video Progress
        React.createElement('div', { style: { marginBottom: '6px' } },
            React.createElement('span', { style: { color: '#888' } }, 'Time: '),
            React.createElement('span', {
                style: { color: '#2196F3', fontWeight: 'bold' }
            }, formatVideoTime(currentTime) + ' / ' + formatVideoTime(duration))
        ),

        // Progress Bar
        React.createElement('div', {
            style: {
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
            }
        },
            React.createElement('div', {
                style: {
                    fontSize: '11px',
                    color: '#888',
                    marginBottom: '4px'
                }
            }, 'Timeout Progress:'),
            React.createElement('div', {
                style: {
                    width: '100%',
                    height: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '3px',
                    overflow: 'hidden',
                }
            },
                React.createElement('div', {
                    style: {
                        width: Math.min(100, (timeSinceActivity / 5000) * 100) + '%',
                        height: '100%',
                        backgroundColor: timeSinceActivity > 5000 ? '#f44336' : timeSinceActivity > 2500 ? '#FF9800' : '#4CAF50',
                        transition: 'width 0.1s linear, background-color 0.3s ease',
                    }
                })
            )
        )
    );
}

export default ControlsDebugOverlay;
